import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";

interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  min?: Date;
  max?: Date;
  className?: string;
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function formatDate(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function getCalendarDays(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const cells: (Date | null)[] = [];
  // leading blanks
  for (let i = 0; i < first.getDay(); i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d));
  // trailing blanks to fill grid
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export function DatePicker({ value, onChange, placeholder = "Pick a date", disabled = false, min, max, className }: DatePickerProps) {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => value ? new Date(value.getFullYear(), value.getMonth(), 1) : new Date(today.getFullYear(), today.getMonth(), 1));
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const openPicker = () => {
    if (disabled) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const dropHeight = 320;
      const top = rect.bottom + 6 + window.scrollY;
      const fitsBelow = rect.bottom + dropHeight < window.innerHeight;
      setPos({
        top: fitsBelow ? top : rect.top - dropHeight - 6 + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setOpen(true);
  };

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const prevMonth = () => setView(v => new Date(v.getFullYear(), v.getMonth() - 1, 1));
  const nextMonth = () => setView(v => new Date(v.getFullYear(), v.getMonth() + 1, 1));

  const selectDay = (d: Date) => {
    onChange?.(d);
    close();
  };

  const clearDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const cells = getCalendarDays(view.getFullYear(), view.getMonth());

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cn("datepicker-trigger", open && "datepicker-trigger-open", disabled && "datepicker-trigger-disabled", className)}
        onClick={openPicker}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg className="datepicker-cal-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className={value ? "datepicker-value" : "datepicker-placeholder"}>
          {value ? formatDate(value) : placeholder}
        </span>
        {value && !disabled && (
          <span className="datepicker-clear" onClick={clearDate} role="button" aria-label="Clear date">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        )}
        {!value && (
          <svg className="datepicker-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>

      {open && createPortal(
        <div
          ref={dropRef}
          className="datepicker-popup"
          style={{ top: pos.top, left: pos.left, minWidth: Math.max(pos.width, 280) }}
          role="dialog"
          aria-label="Date picker calendar"
        >
          <div className="datepicker-header">
            <button className="datepicker-nav" onClick={prevMonth} aria-label="Previous month">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <span className="datepicker-month-label">{MONTHS[view.getMonth()]} {view.getFullYear()}</span>
            <button className="datepicker-nav" onClick={nextMonth} aria-label="Next month">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <div className="datepicker-grid">
            {DAYS.map(d => <div key={d} className="datepicker-day-label">{d}</div>)}
            {cells.map((cell, i) => {
              if (!cell) return <div key={`blank-${i}`} />;
              const isSelected = value && isSameDay(cell, value);
              const isToday = isSameDay(cell, today);
              const isDisabled =
                (min && cell < new Date(min.getFullYear(), min.getMonth(), min.getDate())) ||
                (max && cell > new Date(max.getFullYear(), max.getMonth(), max.getDate()));
              return (
                <button
                  key={cell.toISOString()}
                  type="button"
                  className={cn("datepicker-cell", isSelected && "datepicker-cell-selected", isToday && !isSelected && "datepicker-cell-today", isDisabled && "datepicker-cell-disabled")}
                  onClick={() => !isDisabled && selectDay(cell)}
                  disabled={!!isDisabled}
                  aria-label={formatDate(cell)}
                  aria-pressed={!!isSelected}
                >
                  {cell.getDate()}
                </button>
              );
            })}
          </div>
          <div className="datepicker-footer">
            <button className="datepicker-today-btn" type="button" onClick={() => selectDay(today)}>Today</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
